import { NgClass, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, inject } from '@angular/core';

import { CrmPromoIdsMap, EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionComponent } from './option.component';
import { OutrightsOptionGroups, getOutrightsGrouping } from './outrights-option-group.service';

@Component({
    selector: 'ms-outrights-option-group',
    templateUrl: './outrights-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, SlicePipe, OptionComponent, MarketHelpComponent, IconCustomComponent],
})
export class OutrightsOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;

    optionGroups: OutrightsOptionGroups[];
    headerData: string[];
    trackingSource: TrackingSource;
    expanded = false;
    showMoreLessButtonVisible = false;
    visibleOptionsLimit: number | undefined;

    private readonly logger: SportsRemoteLogger;
    optionsClass: string;
    readonly isEpcotEnabled: boolean;

    private optionGroupTrackingService = inject(OptionGroupTrackingService);

    constructor(
        public sitecore: Sitecore,
        private marketGroupingConfig: MarketGroupingConfig,
        loggerFactory: LoggerFactory,
        epcotConfig: EpcotConfig,
    ) {
        this.logger = loggerFactory.getLogger('OutrightsOptionGroupComponent');
        this.isEpcotEnabled = epcotConfig.isEnabled;
    }

    ngOnChanges(): void {
        this.optionGroups = getOutrightsGrouping(this.optionGroup.options);
        this.headerData = [...new Set(this.optionGroup.options.map((item) => item.optionColumnName || ''))].filter((header) => header !== '');
        this.showMoreLessButtonVisible =
            this.optionGroups.length > (this.optionsLimitOverride ?? this.marketGroupingConfig.optionGroupRowsThreshold.outrights);
        this.calculateMaxNumberOfVisibleOptions();
        this.optionsClass = this.getOptionsClass();
        this.trackingSource = {
            source: this.tracking,
            moduleType: this.trackingModuleType,
        };
    }

    getOption(group: EventOption[], header: string): EventOption | undefined {
        return group.find((opt) => opt.optionColumnName === header);
    }

    trackGroup(index: number, group: OutrightsOptionGroups): number {
        return group.playerId;
    }

    trackHeader(index: number, header: string): string {
        return header;
    }

    onShowMoreLessButtonToggle(): void {
        this.expanded = !this.expanded;
        this.calculateMaxNumberOfVisibleOptions();

        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.expanded ? 'expand' : 'collapse',
            this.event.participants,
            this.optionGroup.name,
            this.optionGroup.name,
        );
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.visibleOptionsLimit = this.expanded
            ? undefined
            : (this.optionsLimitOverride ?? this.marketGroupingConfig.optionGroupRowsThreshold.outrights);
    }

    private getOptionsClass(): string {
        const optionsCount = this.headerData?.length ?? 0;
        switch (optionsCount) {
            case 1:
                return 'double';
            case 2:
                return 'triple';
            case 3:
                return 'quadruple';
            case 4:
                return 'quintuple';
            case 5:
                return 'sextuple';
            default:
                this.logger.error(`Outrights Grouping Error - ${optionsCount} columns!`);

                return '';
        }
    }
}
